import store from "@/store";
import apiService from "./apiService";
import { computed } from "vue";

const apiCache = (() => {
    const cacheStore = new Map();
    const CACHE_TTL = 300000 * 72; // 5 minutes
  
    return {
      get: (key) => {
        const entry = cacheStore.get(key);
        if (!entry) return null;
        
        const now = Date.now();
        return (now - entry.timestamp) < CACHE_TTL ? entry.data : null;
      },
      set: (key, data) => {
        cacheStore.set(key, {
          data,
          timestamp: Date.now()
        });
      },
      clear: (key) => {
        if (key) cacheStore.delete(key);
        else cacheStore.clear();
      },
      clearByPattern: (pattern) => {
        const regex = new RegExp(pattern);
        for (const key of cacheStore.keys()) {
          if (regex.test(key)) cacheStore.delete(key);
        }
      }
    };
  })();
  
  
const areaService = {
    async getMunicipalities() {

        try {
            const currentUser = computed(() => store.getters["auth/currentUser"] ?? null);
            // Return cached data if valid
          const cacheKey = `municipalities_${currentUser.value.syspk_user}`;
          
          const cachedData = apiCache.get(cacheKey);
          if (cachedData) return cachedData;
      
          // Fetch fresh data
          const response = await apiService.get('a/getmunicipalities', {}, true);
          let cities = Array.isArray(response?.data) ? response.data : [];
      
          // Add empty option if multiple cities exist
          if (cities.length > 1) {
            cities = [{ id_mun: 0, name_mun: '' }, ...cities];
          }
      
          // Update cache
          apiCache.set(cacheKey,cities);
          return cities;
      
        } catch (error) {
          console.error('Municipalities fetch error:', error);
          // Return cached data if available, even if stale
          return [];
        }
      },
      
    async getMunicipalitiesWithPolygons() {
        try {
            const cacheKey = 'municipalities_with_polygons';
            const cached = apiCache.get(cacheKey);
            if (cached) return cached;
            
            const cities = (await apiService.get(`a/getmunicipalitieswithpolygon`, {}, true)).data;
            apiCache.set(cacheKey, cities);
            return cities;
        }
        catch {
            return [];
        }
    },
    async getBrgys(id, dontaddPadding) {
        try {
            const cacheKey = `brgys_${id}`;
            const cached = apiCache.get(cacheKey);
            if (cached) {
                return dontaddPadding ? cached : [{ id_brgy: 0, name_brgy: '' }, ...cached];
            }
            
            let brgys = (await apiService.get(`a/getbrgysbycityid/${id}`, {}, true)).data;
            apiCache.set(cacheKey, brgys);
            
            if (!dontaddPadding) {
                brgys = [{ id_brgy: 0, name_brgy: '' }, ...brgys];
            }
            return brgys;
        }
        catch (err) {
            console.error(err);
            return [];
        }
    },
    async getBrgysWithNoPolygon(id) {
        try {
            let brgys = (await apiService.get(`a/getbrgyswnopbycityid/${id}`, {}, true)).data;
            brgys.unshift({ id_brgy: 0, name_brgy: '' });
            return brgys;
        }
        catch (err) {
            console.error(err);
            return [];
        }
    },

    async getSitioWithNoPolygons(id) {
        try {
            let brgys = (await apiService.get(`a/getsitioswnopbybrgyid/${id}`, {}, true)).data;
            brgys.unshift({ id_sitio: 0, name_sitio: '' });
            return brgys;
        }
        catch {
            return [];
        }
    },


    async getBrgysByCityName(city) {
        try {
            let brgys = (await apiService.get(`a/getbrgysbycityname/`, { name: city }, true)).data;
            brgys.unshift({ id_brgy: 0, name_brgy: '' });
            return brgys;
        }
        catch (err) {
            console.error(err);
            return [];
        }
    },
    async getMunicipalAreaVoterSummary(id) {
        try {
            return (await apiService.get(`a/getmunicipalareavotersummary/${id}`, {}, true)).data;
        }
        catch {
            return {};
        }
    },
    async getBrgysWithPolygons() {
        try {
            return (await apiService.get(`a/getbrgysbyboundtouser`, {}, true)).data;
        }
        catch {
            return [];
        }
    },

    async getSitioWithPolygons() {
        try {
            return (await apiService.get(`a/getsitiosboundtouser`, {}, true)).data;
        }
        catch {
            return [];
        }
    },

    async getBrgyAreaVoterSummary(id) {
        try {
            return (await apiService.get(`a/getbrgyvotersummary/${id}`, {}, true)).data;
        }
        catch (err) {
            console.error(err);
            return {};
        }
    },

    async getSitioAreaVoterSummary(id) {
        try {
            return (await apiService.get(`a/getsitiovotersummary/${id}`, {}, true)).data;
        }
        catch (err) {
            console.error(err);
            return {};
        }
    },
    async fetchMarkersFromDatabase(bounds) {
        try {
            return (await apiService.post(`a/gethouseholdsinbound/`, bounds, true)).data;
        }
        catch (err) {
            console.error(err);
            return {};
        }
    },

    async fetchOnlineUsers(bounds) {
        try {
            bounds;
            return (await apiService.get(`u/get-recently-online-users/`, {}, true)).data;
        }
        catch (err) {
            console.error(err);
            return {};
        }
    },
    async getHouseholdsByCoordinates(coordinates) {
        try {
            return (await apiService.post(`a/gethouseholdsbycoordinates/`, coordinates, true)).data;
        }
        catch {
            return [];
        }
    },
    async getHouseholdContentById(id) {
        try {
            return (await apiService.get(`h/gethouseholdcontentwithimagesbyid/${id}`, {}, true)).data;
        }
        catch {
            return [];
        }
    },
    async searchGeoVoter(keyword) {
        try {
            return (await apiService.get(`a/searchgeovoter`, { keyword: keyword }, true)).data;
        }
        catch {
            return [];
        }
    },
    async savePolygonToDatabase(id, type, wkt) {
        try {
            const p = { id_mun: id, area_mun: wkt };
            if (type === "mun") {
                const key = `cities_p${store.getters["auth/currentUser"].syspk_user}`;
                localStorage.removeItem(key);
            }
            return (await apiService.post(`a/savepolygon/${type}`, p, true)).data;
        }
        catch {
            return { success: false, message: "An error occured while processing the request." };
        }
    },
    async searchBrgys(searchModel) {
        try {
            return (await apiService.get(`a/searchbrgysetup`, searchModel, true)).data
        }
        catch {
            return [];
        }
    },
    async getBrgySetup(id) {
        try {
            return (await apiService.get(`a/getbrgysetup/${id}`, {}, true)).data
        }
        catch {
            return [];
        }
    },
    async searchMunicipalities(searchModel) {
        try {
            return (await apiService.get(`a/searchcitysetup`, searchModel, true)).data;
        }
        catch {
            return [];
        }
    },
    async saveCity(city) {
        try {
            return (await apiService.post(`a/savecity`, city, true)).data;
        }
        catch {
            return { succes: false, message: "An error occured while processing the request." };
        }
    },
    async saveBrgy(brgy) {
        try {
            return (await apiService.post(`a/savebrgy`, brgy, true)).data;
        }
        catch {
            return { succes: false, message: "An error occured while processing the request." };
        }
    },
    async getBrgyForUserRoles() {
        try {
            return (await apiService.get(`a/getallowedbrgys`, {}, true)).data;
        }
        catch {
            return [];
        }
    },
    async getSitiosForMerging(brgyid) {
        try {
            return (await apiService.get(`a/getsitios-for-merging/${brgyid}`, {}, true)).data;
        }
        catch {
            return {officialSitios: [], rawSitios: []};
        }
    },
    async mergeSitios(brgyid,request) {
        try {
            return (await apiService.post(`a/merge-sitios/${brgyid}`, request, true)).data;
        }
        catch {
            return {succes: false, message: "An error occured while processing the request."};
        }
    }


};

export default areaService;